import { computed, type ComputedRef, type Ref } from 'vue'
import { useApolloClient, useQuery } from '@vue/apollo-composable'
import gql from 'graphql-tag'
import { articleColorCss, moduleArticleColorMap, sameColorMap } from '@/utils/articleColor'
import { flattenNodes } from '@/composables/useStepMeshHints'
import { ArticleType } from '@/generated/graphql'

type ArticleRef = { article?: { articleNumber: string; type?: string } | null }
type InputRef = { output?: { article?: { articleNumber: string } | null } | null }

export type ColorNode = {
  id: string
  parentId: string | null
  effectiveArticleAllocations: ArticleRef[]
  effectiveInputs: InputRef[]
}

type ColorModule = { id: string; nodes: ColorNode[] }

type ArticleColorsQuery = {
  product: {
    step?: {
      id: string
      articleAllocations: ArticleRef[]
      inputs: InputRef[]
      module: ColorModule | null
    }
    module?: ColorModule
    placementArticleAllocations?: ArticleRef[]
    placementStepInputs?: InputRef[]
  }
}

type ArticleColorsVariables = {
  stepId: string
  moduleId: string
  graphNodeStepId: string
  byModule: boolean
  placement: boolean
}

function articleNumbersOf(allocations: ArticleRef[], inputs: InputRef[]): string[] {
  return [
    ...allocations
      .filter((a) => a.article?.type !== ArticleType.Consumable)
      .map((a) => a.article?.articleNumber),
    ...inputs.map((i) => i.output?.article?.articleNumber),
  ].filter((n): n is string => !!n)
}

function moduleColorSteps(nodes: ColorNode[]): string[][] {
  return flattenNodes(nodes).map((n) =>
    articleNumbersOf(n.effectiveArticleAllocations, n.effectiveInputs),
  )
}

function stableColorMap(steps: () => string[][]): ComputedRef<Map<string, number>> {
  return computed((previous) => {
    const next = moduleArticleColorMap(steps())
    return previous && sameColorMap(previous, next) ? previous : next
  })
}

export function useModuleArticleColorMap(
  nodes: () => ColorNode[],
): ComputedRef<Map<string, number>> {
  return stableColorMap(() => moduleColorSteps(nodes()))
}

export function useStepArticleColors(
  stepId: Ref<string | undefined>,
  graphNodeStepId?: Ref<string | undefined>,
  moduleId?: Ref<string | undefined>,
) {
  const { client } = useApolloClient()
  const byModule = computed(() => !!moduleId?.value)
  const placement = computed(() => !!graphNodeStepId?.value)

  const { result } = useQuery<ArticleColorsQuery, ArticleColorsVariables>(
    gql`
      query StepArticleColors(
        $stepId: ID!
        $moduleId: ID!
        $graphNodeStepId: ID!
        $byModule: Boolean!
        $placement: Boolean!
      ) {
        product {
          step(id: $stepId) @skip(if: $byModule) {
            id
            articleAllocations {
              ...StepArticleColorsAllocation
            }
            inputs {
              ...StepArticleColorsInput
            }
            module {
              ...StepArticleColorsModule
            }
          }
          module(id: $moduleId) @include(if: $byModule) {
            ...StepArticleColorsModule
          }
          placementArticleAllocations(graphNodeStepId: $graphNodeStepId) @include(if: $placement) {
            ...StepArticleColorsAllocation
          }
          placementStepInputs(graphNodeStepId: $graphNodeStepId) @include(if: $placement) {
            ...StepArticleColorsInput
          }
        }
      }

      fragment StepArticleColorsModule on Module {
        id
        nodes {
          id
          parentId
          effectiveArticleAllocations {
            ...StepArticleColorsAllocation
          }
          effectiveInputs {
            ...StepArticleColorsInput
          }
        }
      }

      fragment StepArticleColorsAllocation on ArticleAllocation {
        __typename
        ... on ManualArticleAllocation {
          article {
            articleNumber
            type
          }
        }
        ... on EbomPartAllocation {
          article {
            articleNumber
            type
          }
        }
      }

      fragment StepArticleColorsInput on StepInput {
        output {
          article {
            articleNumber
          }
        }
      }
    `,
    () => ({
      stepId: stepId.value || '',
      moduleId: moduleId?.value || '',
      graphNodeStepId: graphNodeStepId?.value || '',
      byModule: byModule.value,
      placement: placement.value,
    }),
    () => ({ enabled: !!stepId.value || !!moduleId?.value }),
  )

  const colorMap = stableColorMap(() => {
    const product = result.value?.product
    if (!product) return []

    const module = product.module ?? (placement.value ? null : product.step?.module)
    if (module) return moduleColorSteps(module.nodes)

    if (placement.value) {
      return [
        articleNumbersOf(
          product.placementArticleAllocations ?? [],
          product.placementStepInputs ?? [],
        ),
      ]
    }

    const step = product.step
    return step ? [articleNumbersOf(step.articleAllocations, step.inputs)] : []
  })

  const swatchColor = (articleNumber: string) => {
    const index = colorMap.value.get(articleNumber)
    return index === undefined ? 'transparent' : articleColorCss(index)
  }

  const refresh = () => client.refetchQueries({ include: ['StepArticleColors'] })

  return { colorMap, swatchColor, refresh }
}
